import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { parseFile, type ColumnMeta, type ParsedDataset, type Row } from '@/lib/biData';
import { STORE_SOURCES, buildStoreDataset, type StoreSourceKey } from '@/lib/biStoreSources';
import DataPreview from './components/DataPreview';
import AutoDashboard from './components/AutoDashboard';
import BiChat from './components/BiChat';
import { DashboardBuilder, BiModelPanel } from './components/DashboardBuilder';
import FiltersPanel from './components/FiltersPanel';
import { applyBiFilters, EMPTY_FILTERS, filterKey, type BiFilterState } from '@/lib/biFilters';

// ============================================================
// Zifek BI — Business Intelligence pour les commerçants.
// Import de fichiers (CSV, Excel, JSON), aperçu des données,
// tableaux de bord + graphiques automatiques, insights IA et
// chatbot IA sur les données. Sources et dashboards sont isolés
// par commerce (bi_datasets / bi_dashboards).
// ============================================================

interface DatasetMeta {
  id: number;
  name: string;
  filename: string | null;
  file_type: string | null;
  size_bytes: number | null;
  row_count: number | null;
  columns: ColumnMeta[];
  created_at: string;
}

type Tab = 'preview' | 'dashboard' | 'builder' | 'model' | 'filters' | 'chat';

interface PendingImport {
  name: string;
  filename: string;
  fileType: string;
  size: number;
  parsed: ParsedDataset;
}

interface SavedDashboard {
  id: number;
  name: string;
  dataset_id: number | null;
  config: { template?: string; widgets?: unknown[] } | null;
}

const ACCEPT = '.csv,.xlsx,.xls,.json';

function fileTypeOf(name: string): string {
  return (name.split('.').pop() || '').toLowerCase();
}

export default function ZifekBIPage() {
  const { user } = useAuth();
  const [sources, setSources] = useState<DatasetMeta[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [dataset, setDataset] = useState<ParsedDataset | null>(null);
  const [datasetName, setDatasetName] = useState('');
  const [tab, setTab] = useState<Tab>('preview');
  const [loadingSources, setLoadingSources] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<PendingImport | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [notice, setNotice] = useState('');
  const [storeLoading, setStoreLoading] = useState<StoreSourceKey | null>(null);
  const [filterState, setFilterState] = useState<BiFilterState>(EMPTY_FILTERS);
  const [dashboards, setDashboards] = useState<SavedDashboard[]>([]);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const filteredRows = useMemo(
    () => applyBiFilters(dataset?.rows || [], filterState),
    [dataset, filterState],
  );

  const handleCrossFilter = (column: string, value: string) => {
    setFilterState((prev) => {
      const id = filterKey(column, value);
      if (prev.filters.some((f) => f.id === id)) return prev;
      return { ...prev, filters: [...prev.filters, { id, column, value }] };
    });
  };

  const loadSources = useCallback(async () => {
    if (!user) return;
    setLoadingSources(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('bi_datasets')
        .select('id, name, filename, file_type, size_bytes, row_count, columns, created_at')
        .eq('idcommerce', commerceId)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setSources((data as DatasetMeta[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des sources');
    } finally {
      setLoadingSources(false);
    }
  }, [user, commerceId]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const loadDashboards = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('bi_dashboards')
        .select('id, name, dataset_id, config')
        .eq('idcommerce', commerceId)
        .order('updated_at', { ascending: false });
      setDashboards((data || []) as SavedDashboard[]);
    } catch {
      setDashboards([]);
    }
  }, [user, commerceId]);

  useEffect(() => { void loadDashboards(); }, [loadDashboards]);

  const selectSource = async (id: number) => {
    setSelectedId(id);
    setFilterState(EMPTY_FILTERS);
    setLoadingData(true);
    setDataset(null);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('bi_datasets')
        .select('name, columns, rows')
        .eq('id', id)
        .maybeSingle();
      if (err) throw err;
      if (data) {
        setDataset({ columns: (data.columns as ColumnMeta[]) || [], rows: (data.rows as Row[]) || [] });
        setDatasetName(data.name as string);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des données');
    } finally {
      setLoadingData(false);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError('');
    try {
      const file = files[0];
      const parsed = await parseFile(file);
      setPending({
        name: file.name.replace(/\.[^.]+$/, ''),
        filename: file.name,
        fileType: fileTypeOf(file.name),
        size: file.size,
        parsed,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fichier illisible');
    }
  };

  const savePending = async () => {
    if (!pending || !user) return;
    setImporting(true);
    setError('');
    setNotice('');
    try {
      // On insère par paliers : si le volume est trop lourd pour la requête,
      // on retente avec moins de lignes plutôt que d’échouer complètement.
      const attempts = [pending.parsed.rows.length, 2000, 800, 200];
      let lastError: unknown = null;
      let insertedId: number | null = null;
      let keptRows = pending.parsed.rows.length;

      for (const limit of attempts) {
        if (limit <= 0) break;
        const rows = pending.parsed.rows.slice(0, limit);
        try {
          const { data, error: err } = await supabase
            .from('bi_datasets')
            .insert({
              idcommerce: commerceId,
              name: pending.name.trim() || pending.filename,
              filename: pending.filename,
              file_type: pending.fileType,
              size_bytes: pending.size,
              row_count: rows.length,
              columns: pending.parsed.columns,
              rows,
            })
            .select('id')
            .maybeSingle();
          if (err) throw err;
          if (!data?.id) throw new Error("La source n'a pas été enregistrée (réponse vide du serveur).");
          insertedId = data.id as number;
          keptRows = rows.length;
          break;
        } catch (attemptError) {
          lastError = attemptError;
          if (rows.length === limit && limit === pending.parsed.rows.length) continue;
          continue;
        }
      }

      if (!insertedId) throw lastError instanceof Error ? lastError : new Error('Enregistrement impossible.');

      setPending(null);
      await loadSources();
      void selectSource(insertedId);
      const truncated = keptRows < pending.parsed.rows.length;
      setNotice(
        truncated
          ? `Source enregistrée avec ${keptRows.toLocaleString('fr-FR')} lignes (les ${(pending.parsed.rows.length - keptRows).toLocaleString('fr-FR')} lignes supplémentaires dépassaient la limite d’un seul import).`
          : `Source enregistrée : ${keptRows.toLocaleString('fr-FR')} lignes importées dans la base.`,
      );
      window.setTimeout(() => setNotice(''), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur d’enregistrement de la source');
    } finally {
      setImporting(false);
    }
  };

  /** Charge une source interne de la boutique dans Zifek BI. */
  const loadStoreSource = async (key: StoreSourceKey) => {
    setStoreLoading(key);
    setError('');
    setNotice('');
    try {
      const built = await buildStoreDataset(key, commerceId);
      if (built.dataset.rows.length === 0) {
        setError(`Aucune donnée à analyser pour « ${built.name} » pour le moment.`);
        return;
      }
      // Remplace la version précédente de la même source boutique.
      await supabase.from('bi_datasets').delete().eq('idcommerce', commerceId).eq('name', built.name);
      const { data, error: err } = await supabase
        .from('bi_datasets')
        .insert({
          idcommerce: commerceId,
          name: built.name,
          filename: built.name,
          file_type: 'shop',
          size_bytes: JSON.stringify(built.dataset.rows).length,
          row_count: built.dataset.rows.length,
          columns: built.dataset.columns,
          rows: built.dataset.rows,
        })
        .select('id')
        .maybeSingle();
      if (err) throw err;
      await loadSources();
      if (data?.id) void selectSource(data.id as number);
      setNotice(`${built.name} chargée : ${built.dataset.rows.length.toLocaleString('fr-FR')} lignes.`);
      window.setTimeout(() => setNotice(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des données boutique');
    } finally {
      setStoreLoading(null);
    }
  };

  const deleteSource = async (id: number) => {
    try {
      await supabase.from('bi_datasets').delete().eq('id', id);
      setSources((prev) => prev.filter((s) => s.id !== id));
      if (selectedId === id) { setSelectedId(null); setDataset(null); }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  /**
   * Modèle « Ventes de la boutique » : recharge les ventes réelles puis
   * enregistre (ou met à jour) un dashboard BI prêt à l'emploi. Rejouer le
   * modèle d'un clic recharge les données et ouvre l'analyse.
   */
  const applySalesTemplate = async () => {
    if (!user) return;
    setApplyingTemplate(true);
    setError('');
    setNotice('');
    try {
      const templateName = 'Ventes de la boutique';
      const built = await buildStoreDataset('ventes', commerceId);
      if (built.dataset.rows.length === 0) {
        setError('Aucune vente à analyser pour le moment. Enregistrez des commandes puis réessayez.');
        return;
      }

      await supabase.from('bi_datasets').delete().eq('idcommerce', commerceId).eq('name', built.name);
      const { data: inserted, error: insertError } = await supabase
        .from('bi_datasets')
        .insert({
          idcommerce: commerceId,
          name: built.name,
          filename: built.name,
          file_type: 'shop',
          size_bytes: JSON.stringify(built.dataset.rows).length,
          row_count: built.dataset.rows.length,
          columns: built.dataset.columns,
          rows: built.dataset.rows,
        })
        .select('id')
        .maybeSingle();
      if (insertError) throw insertError;
      const datasetId = Number(inserted?.id || 0) || null;

      const widgets = [
        { id: 'tpl-sales-kpi-1', type: 'kpi', title: 'Chiffre d’affaires', chartType: 'bar', dimension: '', measure: 'Montant', agg: 'SUM', x: 0, y: 0, w: 3, h: 2 },
        { id: 'tpl-sales-kpi-2', type: 'kpi', title: 'Encaissé', chartType: 'bar', dimension: '', measure: 'Montant payé', agg: 'SUM', x: 3, y: 0, w: 3, h: 2 },
        { id: 'tpl-sales-kpi-3', type: 'kpi', title: 'Restant à encaisser', chartType: 'bar', dimension: '', measure: 'Restant', agg: 'SUM', x: 6, y: 0, w: 3, h: 2 },
        { id: 'tpl-sales-kpi-4', type: 'kpi', title: 'Commandes', chartType: 'bar', dimension: '', measure: 'Numéro', agg: 'COUNT', x: 9, y: 0, w: 3, h: 2 },
        { id: 'tpl-sales-line', type: 'chart', title: 'Évolution du chiffre d’affaires', chartType: 'line', dimension: 'Date', measure: 'Montant', agg: 'SUM', x: 0, y: 2, w: 8, h: 4 },
        { id: 'tpl-sales-pie', type: 'chart', title: 'Répartition par moyen de paiement', chartType: 'pie', dimension: 'Paiement', measure: 'Montant', agg: 'SUM', x: 8, y: 2, w: 4, h: 4 },
        { id: 'tpl-sales-client', type: 'chart', title: 'Top clients', chartType: 'bar', dimension: 'Client', measure: 'Montant', agg: 'SUM', x: 0, y: 6, w: 6, h: 4 },
        { id: 'tpl-sales-type', type: 'chart', title: 'Ventes par type', chartType: 'bar', dimension: 'Type', measure: 'Montant', agg: 'SUM', x: 6, y: 6, w: 6, h: 4 },
      ];

      const { data: existing } = await supabase
        .from('bi_dashboards')
        .select('id')
        .eq('idcommerce', commerceId)
        .eq('name', templateName)
        .maybeSingle();

      const payload = {
        idcommerce: commerceId,
        name: templateName,
        dataset_id: datasetId,
        config: { template: 'ventes-boutique', widgets },
        updated_at: new Date().toISOString(),
      };
      if (existing?.id) {
        const { error: updateError } = await supabase.from('bi_dashboards').update(payload).eq('id', existing.id);
        if (updateError) throw updateError;
      } else {
        const { error: createError } = await supabase.from('bi_dashboards').insert(payload);
        if (createError) throw createError;
      }

      await loadSources();
      await loadDashboards();
      if (datasetId) void selectSource(datasetId);
      setTab('builder');
      setNotice(`Modèle « ${templateName} » prêt : ${built.dataset.rows.length.toLocaleString('fr-FR')} ventes chargées et dashboard enregistré.`);
      window.setTimeout(() => setNotice(''), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de préparer le modèle de dashboard.');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const openSavedDashboard = async (dashboard: SavedDashboard) => {
    if (!dashboard.dataset_id) {
      setError('La source de ce dashboard n\u2019existe plus. Rejouez le modèle pour la recréer.');
      return;
    }
    await selectSource(dashboard.dataset_id);
    setTab('builder');
  };

  const fmtSize = (bytes: number | null) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            <i className="ri-bar-chart-box-line mr-2 text-primary-500"></i>Zifek BI
          </h2>
          <p className="text-sm text-foreground-500 mt-1">Business Intelligence : importez vos données, analysez-les avec l’IA</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-upload-cloud-2-line"></i>Importer des données
        </button>
        <input ref={fileInputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { void handleFiles(e.target.files); e.target.value = ''; }} />
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      {notice && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-secondary-50 text-secondary-800 text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{notice}
          <button onClick={() => setNotice('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      {/* Sources internes de la boutique */}
      <div className="mb-6 bg-background-50 border border-background-200/70 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-store-2-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-900">Données de la boutique</p>
            <p className="text-[11px] text-foreground-500">Analysez directement vos ventes, produits, services, clients et factures</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2">
          {STORE_SOURCES.map((s) => (
            <button
              key={s.key}
              onClick={() => void loadStoreSource(s.key)}
              disabled={storeLoading !== null}
              className="text-left p-3 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-primary-50/40 transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={`${storeLoading === s.key ? 'ri-loader-4-line animate-spin' : s.icon} text-base text-primary-600`}></i>
              <p className="text-xs font-semibold text-foreground-900 mt-1.5 leading-tight">{s.label}</p>
              <p className="text-[10px] text-foreground-500 mt-0.5 leading-tight">{s.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Modèles de dashboards réutilisables */}
      <div className="mb-6 bg-background-50 border border-background-200/70 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-bookmark-3-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-900">Modèles de dashboard</p>
            <p className="text-[11px] text-foreground-500">Un dashboard complet prêt à l&apos;emploi, rechargeable d&apos;un clic avec vos données à jour</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <button
            onClick={() => void applySalesTemplate()}
            disabled={applyingTemplate}
            className="text-left p-3 rounded-lg border border-primary-200/70 bg-primary-50/40 hover:border-primary-300 transition-colors cursor-pointer disabled:opacity-50"
          >
            <div className="flex items-center gap-2">
              <i className={`${applyingTemplate ? 'ri-loader-4-line animate-spin' : 'ri-shopping-bag-3-line'} text-primary-600 text-base`}></i>
              <span className="text-xs font-semibold text-foreground-900">Ventes de la boutique</span>
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">Modèle</span>
            </div>
            <p className="text-[10px] text-foreground-500 mt-1 leading-snug">
              CA, encaissé, restant, évolution dans le temps, moyens de paiement, top clients et types de ventes.
            </p>
          </button>
          {dashboards
            .filter((d) => d.config?.template !== 'ventes-boutique')
            .slice(0, 3)
            .map((d) => (
              <button
                key={d.id}
                onClick={() => void openSavedDashboard(d)}
                className="text-left p-3 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <i className="ri-dashboard-3-line text-foreground-500 text-base"></i>
                  <span className="text-xs font-semibold text-foreground-900 truncate">{d.name}</span>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">Enregistré</span>
                </div>
                <p className="text-[10px] text-foreground-500 mt-1">Ouvrir ce dashboard et sa source de données.</p>
              </button>
            ))}
        </div>
      </div>

      {/* Zone glisser-déposer */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); void handleFiles(e.dataTransfer.files); }}
        className={`mb-6 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? 'border-primary-400 bg-primary-50/50' : 'border-background-300/60 bg-background-50'}`}
      >
        <i className="ri-drag-drop-line text-2xl text-foreground-400"></i>
        <p className="text-sm text-foreground-600 mt-2">Glissez-déposez un fichier ici, ou cliquez sur « Importer des données »</p>
        <p className="text-xs text-foreground-400 mt-1">Formats acceptés : CSV, Excel (.xlsx/.xls), JSON</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Liste des sources */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            Mes sources ({sources.length})
          </div>
          {loadingSources ? (
            <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
          ) : sources.length === 0 ? (
            <p className="text-xs text-foreground-400 py-8 text-center px-4">Aucune source importée</p>
          ) : (
            <div className="divide-y divide-background-200/70 max-h-[520px] overflow-y-auto">
              {sources.map((s) => (
                <button
                  key={s.id}
                  onClick={() => void selectSource(s.id)}
                  className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${selectedId === s.id ? 'bg-primary-50/60' : 'hover:bg-background-100'}`}
                >
                  <div className="flex items-center gap-2">
                    <i className={`${s.file_type === 'json' ? 'ri-braces-line' : s.file_type === 'csv' ? 'ri-file-text-line' : 'ri-file-excel-2-line'} text-foreground-500`}></i>
                    <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">{s.name}</span>
                    <span
                      onClick={(e) => { e.stopPropagation(); void deleteSource(s.id); }}
                      className="text-foreground-300 hover:text-red-500 cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line text-xs"></i>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-foreground-400">
                    <span>{(s.row_count || 0).toLocaleString('fr-FR')} lignes</span>
                    <span>·</span>
                    <span>{(s.columns?.length || 0)} colonnes</span>
                    <span className="ml-auto">{fmtSize(s.size_bytes)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Zone principale */}
        <div>
          {!selectedId ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl flex flex-col items-center justify-center py-20 text-center">
              <i className="ri-bar-chart-2-line text-4xl text-foreground-300 mb-3"></i>
              <p className="text-sm text-foreground-600 font-medium">Sélectionnez une source de données</p>
              <p className="text-xs text-foreground-400 mt-1">ou importez un fichier CSV / Excel / JSON pour commencer l’analyse</p>
            </div>
          ) : loadingData ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
          ) : dataset ? (
            <>
              {/* Tabs */}
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 w-fit mb-5">
                {([
                  { key: 'preview', label: 'Aperçu', icon: 'ri-table-line' },
                  { key: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-3-line' },
                  { key: 'builder', label: 'Builder', icon: 'ri-layout-grid-line' },
                  { key: 'filters', label: 'Filtres & dates', icon: 'ri-filter-3-line' },
                  { key: 'model', label: 'Modèle', icon: 'ri-links-line' },
                  { key: 'chat', label: 'Chat IA', icon: 'ri-robot-2-line' },
                ] as const).map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === t.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                  >
                    <i className={t.icon}></i>{t.label}
                  </button>
                ))}
              </div>

              {(filterState.filters.length > 0 || filterState.date) && tab !== 'filters' && (
                <div className="mb-4 px-4 py-2.5 rounded-lg bg-primary-50 border border-primary-200/60 text-xs text-primary-800 flex items-center gap-2 flex-wrap">
                  <i className="ri-filter-3-line"></i>
                  <span>{filteredRows.length.toLocaleString('fr-FR')} / {dataset.rows.length.toLocaleString('fr-FR')} lignes après filtres</span>
                  <button
                    onClick={() => setFilterState(EMPTY_FILTERS)}
                    className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 text-primary-700 font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line"></i>Effacer les filtres
                  </button>
                </div>
              )}

              {tab === 'preview' && <DataPreview columns={dataset.columns} rows={filteredRows} datasetName={datasetName} />}
              {tab === 'dashboard' && <AutoDashboard columns={dataset.columns} rows={filteredRows} datasetName={datasetName} onCrossFilter={handleCrossFilter} />}
              {tab === 'builder' && (
                <DashboardBuilder
                  columns={dataset.columns}
                  rows={filteredRows}
                  datasetId={selectedId}
                  datasetName={datasetName}
                  commerceId={commerceId}
                />
              )}
              {tab === 'filters' && (
                <FiltersPanel
                  columns={dataset.columns}
                  rows={dataset.rows}
                  state={filterState}
                  onChange={setFilterState}
                  filteredCount={filteredRows.length}
                  totalCount={dataset.rows.length}
                  datasetId={selectedId}
                  datasetName={datasetName}
                  commerceId={commerceId}
                />
              )}
              {tab === 'model' && (
                <BiModelPanel
                  sources={sources.map((s) => ({ id: s.id, name: s.name, columns: s.columns || [] }))}
                  columns={dataset.columns}
                  rows={dataset.rows}
                  onApplyField={(_name, newRows, newColumn) => {
                    setDataset((prev) => prev ? {
                      columns: [...prev.columns.filter((c) => c.name !== newColumn.name), newColumn],
                      rows: newRows,
                    } : prev);
                  }}
                />
              )}
              {tab === 'chat' && <BiChat columns={dataset.columns} rows={filteredRows} datasetName={datasetName} />}
            </>
          ) : null}
        </div>
      </div>

      {/* Modale confirmation d'import */}
      {pending && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setPending(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
                <span className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-file-add-line text-primary-500"></i>Nouvelle source de données</span>
                <button onClick={() => setPending(null)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Nom de la source</label>
                  <input
                    value={pending.name}
                    onChange={(e) => setPending({ ...pending, name: e.target.value })}
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-background-100/50 rounded-lg p-3">
                    <p className="text-lg font-bold text-foreground-950">{pending.parsed.rows.length.toLocaleString('fr-FR')}</p>
                    <p className="text-[11px] text-foreground-500">lignes</p>
                  </div>
                  <div className="bg-background-100/50 rounded-lg p-3">
                    <p className="text-lg font-bold text-foreground-950">{pending.parsed.columns.length}</p>
                    <p className="text-[11px] text-foreground-500">colonnes</p>
                  </div>
                  <div className="bg-background-100/50 rounded-lg p-3">
                    <p className="text-lg font-bold text-foreground-950">{fmtSize(pending.size)}</p>
                    <p className="text-[11px] text-foreground-500">taille</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 flex items-center gap-1.5"><i className="ri-information-line"></i>Le fichier sera analysé et stocké dans vos sources BI.</p>
                <div className="flex items-center gap-3">
                  <button onClick={savePending} disabled={importing} className="flex-1 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer flex items-center justify-center gap-2">
                    {importing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                    Enregistrer la source
                  </button>
                  <button onClick={() => setPending(null)} className="px-5 py-2.5 rounded-full bg-background-100 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer">Annuler</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}